<template>
  <v-container style="height: 100%">
    <h3>{{ $tc('location_map')}}: {{ this.customerContext.name }}</h3>
    <gmap-map id="location-map" ref="map" :zoom="12" :center="dummyCenter" v-if="locationsLoaded"/>
    <v-progress-linear v-else indeterminate class="mt-3"></v-progress-linear>
  </v-container>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { gmapApi } from 'vue2-google-maps'
import MarkerClusterer from 'marker-clusterer-plus'
import { each, filter } from 'lodash'

const clusterStyles = [
  {
    url: '/img/map-marker-16.png',
    height: 16,
    width: 16
  },
  {
    url: '/img/map-marker-24.png',
    height: 24,
    width: 24
  },
  {
    url: '/img/map-marker-32.png',
    height: 32,
    width: 32
  }
]

export default {
  name: 'LocationMap',
  mixins: [
    CurrentUserMixin
  ],
  data () {
    return {
      locationTypes: [],
      locations: [],
      locationsLoaded: false,
      // vue-google-maps complains if no center is defined. We'll set the true bounds once the locations are loaded
      dummyCenter: { lat: 0, lng: 0 }
    }
  },
  computed: {
    /**
     * Expose imported gmapApi symbol as 'google' so we can use 'google.maps' object inside Vue as we would normally in
     * any other Javascript project.
     * (As recommended by the vue-google-maps documentation)
     */
    google: gmapApi
  },
  methods: {
    /**
     * Initialize the component - will fetch locations then populate the map.
     */
    async initialize () {
      await this.fetchLocations()
      // Initialize the google maps state once the map promise is resolved.
      this.$refs.map.$mapPromise.then((map) => {
        const bounds = new this.google.maps.LatLngBounds()
        const infoWindow = new this.google.maps.InfoWindow()
        const markers = []

        each(this.locations, location => {
          const latlng = new this.google.maps.LatLng(location.latitude, location.longitude)
          bounds.extend(latlng)

          const marker = new this.google.maps.Marker({
            position: latlng,
            title: location.label,
            map: map,
            zIndex: 100,
            icon: '/img/map-measle.png'
          })
          let infoWindowContent = `<h6><a href="/location/${location.id}">${location.label}</a></h6>`
          if (location.code) {
            infoWindowContent += `${this.$tc('code')}: ${location.code}<br>`
          }
          infoWindowContent += `${this.$tc('type')}: ${this.$tc(location.type + '_location_type')}`

          this.google.maps.event.addListener(marker, 'click', () => {
            infoWindow.close()
            infoWindow.setContent(infoWindowContent)
            infoWindow.open(map, marker)
          })

          markers.push(marker)
        })

        // eslint-disable-next-line no-new
        new MarkerClusterer(map, markers, {
          maxZoom: 13,
          minimumClusterSize: 4,
          gridSize: 35,
          averageCenter: true,
          styles: clusterStyles
        })

        map.fitBounds(bounds)
        map.panToBounds(bounds)
      })
    },
    /**
     * Retrieve location data.
     * @returns {Promise<T>}
     */
    fetchLocations () {
      this.locationsLoaded = false
      const requestParams = {
        fields: 'id,label,latitude,longitude,code,type',
        'per-page': 0,
        customer_id: this.customerContextId
      }
      return this.$orm('Location').findAll(requestParams)
        .then(resp => {
          // Filter out any ignored locations
          this.locations = filter(resp.getItems(), x => x.type !== 'ignored')
          this.locationsLoaded = true
        })
        .catch(error => {
          this.handleError(error)
        })
    },
    customerContextChangeHandler (newCustomerContext, oldCustomerContext) {
      this.initialize()
    }
  },
  created () {
    this.initialize()
  }
}
</script>

<style scoped lang="scss">
#location-map {
  height: calc(100% - 80px);
}
</style>
